/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { message, Row, Col } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type AudiCalculationModuleSettingDataFragment } from '../../../../../api/fragments/AudiCalculationModuleSettingData';
import { type AudiCalculationModuleSpecsFragment } from '../../../../../api/fragments/AudiCalculationModuleSpecs';
import {
    UpdateAudiCalculationModuleSettingDocument,
    type UpdateAudiCalculationModuleSettingMutation,
    type UpdateAudiCalculationModuleSettingMutationVariables,
} from '../../../../../api/mutations/updateAudiCalculationModuleSetting';
import { type AudiCalculationSettingUpdateInput, ModuleType } from '../../../../../api/types';
import { useCompany } from '../../../../../components/contexts/CompanyContextManager';
import { useDealerContext } from '../../../../../components/contexts/DealerContextManager';
import DealershipPriceDisclaimerField from '../../../../../components/fields/DealershipFields/DealershipPriceDisclaimer';
import Form from '../../../../../components/fields/Form';
import InputField from '../../../../../components/fields/InputField';
import SelectField from '../../../../../components/fields/SelectField';
import useHandleError from '../../../../../utilities/useHandleError';
import useValidator from '../../../../../utilities/useValidator';
import MarketTypeGroup from '../../modules/implementations/shared/MarketTypeGroup';
import { getFinancingApplicationMarket } from '../../StandardApplicationModulePage/StandardApplicationModuleMainDetails';
import usePriceDisclaimerToolTipByMarket from '../../usePriceDisclaimerToolTipByMarket';
import { updateValidator } from './validators';

type AudiCalculationSettingTabMainDetailsProps = {
    module: AudiCalculationModuleSpecsFragment;
    setting: AudiCalculationModuleSettingDataFragment;
};

const StyledContainer = styled(Row)`
    background-color: #fff;
    padding: 20px;
`;

const Fields = ({
    insuranceModuleId: initialInsuranceValue,
}: Pick<AudiCalculationSettingUpdateInput, 'insuranceModuleId'>) => {
    const {
        values: { market },
    } = useFormikContext<AudiCalculationSettingUpdateInput>();

    const company = useCompany(true);
    const { dealersFromApi } = useDealerContext();

    const availableInsuranceModules = useMemo(() => {
        if (!company?.modules) {
            return [];
        }

        const insuranceModuleList = company.modules.filter(module => module.__typename === 'InsuranceModule');

        return insuranceModuleList.map(insuranceModule => ({
            value: insuranceModule.id,
            label: insuranceModule.displayName,
        }));
    }, [company]);

    const priceDisclaimerToolTip = usePriceDisclaimerToolTipByMarket(market);
    const priceDisclaimerItemProps = useMemo(() => ({ tooltip: priceDisclaimerToolTip }), [priceDisclaimerToolTip]);
    const { t } = useTranslation(['moduleDetails']);

    return (
        <StyledContainer gutter={10}>
            <Col md={8} xs={24}>
                <InputField
                    {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                    name="displayName"
                    required
                />
            </Col>
            <Col md={8} xs={24}>
                <InputField
                    {...t('moduleDetails:fields.audiCalculationSettingId', { returnObjects: true })}
                    name="identifier"
                    required
                />
            </Col>
            <Col md={8} xs={24}>
                <DealershipPriceDisclaimerField
                    {...t('moduleDetails:fields.priceDisclaimer', { returnObjects: true })}
                    dealers={dealersFromApi}
                    itemProps={priceDisclaimerItemProps}
                    name="priceDisclaimer"
                />
            </Col>
            <Col md={8} xs={24}>
                <DealershipPriceDisclaimerField
                    {...t('moduleDetails:fields.preownedPriceDisclaimer', { returnObjects: true })}
                    dealers={dealersFromApi}
                    itemProps={priceDisclaimerItemProps}
                    name="preownedPriceDisclaimer"
                />
            </Col>
            <MarketTypeGroup
                dealers={dealersFromApi}
                moduleType={ModuleType.AudiCalculationModule}
                name="market"
                required
            />
            <Col md={8} xs={24}>
                <SelectField
                    {...t('moduleDetails:fields.insuranceModuleId', { returnObjects: true })}
                    allowClear={!initialInsuranceValue}
                    disabled={!!initialInsuranceValue}
                    name="insuranceModuleId"
                    options={availableInsuranceModules}
                    showSearch
                />
            </Col>
        </StyledContainer>
    );
};

const AudiCalculationSettingTabMainDetails = ({ module, setting }: AudiCalculationSettingTabMainDetailsProps) => {
    const { t } = useTranslation('audiCalculationModuleDetails');

    const initialValues = useMemo<AudiCalculationSettingUpdateInput>(
        () => ({
            ...pick(['displayName', 'market', 'insuranceModuleId', 'identifier'], setting),
            priceDisclaimer: setting.dealerPriceDisclaimer,
            preownedPriceDisclaimer: setting.preownedDealerPriceDisclaimer,
            marketType: getFinancingApplicationMarket(setting.marketType),
        }),
        [setting]
    );

    const validate = useValidator(updateValidator);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<AudiCalculationSettingUpdateInput>(
        async values => {
            message.loading({
                content: t('audiCalculationModuleDetails:settings.update.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            const settings = values;

            if (settings.marketType.singapore && !settings.marketType.singapore.coe.overrides) {
                settings.marketType.singapore.coe.overrides = [];
            }

            // submit creation
            await apolloClient
                .mutate<
                    UpdateAudiCalculationModuleSettingMutation,
                    UpdateAudiCalculationModuleSettingMutationVariables
                >({
                    mutation: UpdateAudiCalculationModuleSettingDocument,
                    variables: {
                        moduleId: module.id,
                        settingId: setting.id,
                        settings,
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('audiCalculationModuleDetails:settings.update.messages.success'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, setting.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit, errors }) => (
                <Form id="updateMainDetails" name="updateMainDetails" onSubmitCapture={handleSubmit}>
                    <Fields insuranceModuleId={initialValues.insuranceModuleId} />
                </Form>
            )}
        </Formik>
    );
};

export default AudiCalculationSettingTabMainDetails;
